/**
 * useCrossSystemOccupancy Composable
 *
 * Helpers for fetching and rendering cross-system occupancy events.
 * These are shows scheduled on OTHER DSL systems (e.g. VS) that block pods
 * during a given date range, displayed as non-interactive diagonal-stripe
 * blocks on the Pod Schedule and Scheduler pages.
 *
 * Endpoint: GET /{site_id}/dsl/scheduler/cross_system_occupancy
 *   ?starttime=YYYY-MM-DDT00:00:00-07:00
 *   &endtime=YYYY-MM-DDT23:59:59-07:00
 *
 * Uses the same Amplify proxy transport as setSchedulerSessions.
 *
 * Usage (Options API, spread inside methods):
 *   ...useCrossSystemOccupancy()
 */

import moment from 'moment';

export function useCrossSystemOccupancy() {
  /**
   * Resolve the active site id from the store.
   * @param {Object} store - this.$store
   * @returns {string}
   */
  const getActiveSiteId = (store) =>
    store.state.selectedSiteForScheduler?.site_id || store.state.selected_site_id;

  /**
   * Fetch cross-system occupancy for a single day (Pod Schedule view).
   *
   * @param {Object} store  - this.$store
   * @param {string} date   - 'YYYY-MM-DD'
   * @param {string} [siteId] - Explicit site id; defaults to the active site in
   *                            the store. Pass it when switching locations so the
   *                            request can't race the store update.
   */
  const loadCrossSystemDay = (store, date, siteId) => {
    if (!date) return;
    const selected_site_id = siteId || getActiveSiteId(store);
    if (!selected_site_id) return;
    store.dispatch('fetchCrossSystemOccupancy', {
      selected_site_id,
      startDate: date,
      endDate: date,
    });
  };

  /**
   * Fetch cross-system occupancy for an arbitrary date range (Scheduler view).
   *
   * @param {Object} store      - this.$store
   * @param {string} startDate  - 'YYYY-MM-DD'
   * @param {string} endDate    - 'YYYY-MM-DD'
   * @param {string} [siteId]   - Explicit site id; defaults to the active site in
   *                              the store. Pass it when switching locations so the
   *                              request can't race the store update.
   */
  const loadCrossSystemRange = (store, startDate, endDate, siteId) => {
    if (!startDate || !endDate) return;
    const selected_site_id = siteId || getActiveSiteId(store);
    if (!selected_site_id) return;
    store.dispatch('fetchCrossSystemOccupancy', {
      selected_site_id,
      startDate,
      endDate,
    });
  };

  /**
   * Return cross-system events that fall on a specific calendar date,
   * for the Pod Schedule card list.
   *
   * @param {Array}  events - store.state.crossSystemEvents
   * @param {string} date   - 'YYYY-MM-DD'
   * @returns {Array}
   */
  const getCrossSystemEventsForDate = (events, date) => {
    if (!Array.isArray(events) || !date) return [];
    return events.filter((ev) => moment(ev.start_timestamp).format('YYYY-MM-DD') === date);
  };

  /**
   * Convert a normalised cross-system event into a FullCalendar event
   * descriptor with stripe styling. Non-interactive (display-only).
   *
   * @param {Object} ev - Item from store.state.crossSystemEvents
   * @returns {Object}  FullCalendar event
   */
  const toFullCalendarEvent = (ev) => ({
    id: `cross-system-${ev.session_id || ev.id || Math.random()}`,
    // e.g. "Entropy Act 3 · VS"
    title: `${ev.title_name || ev.title || 'Occupied'} · Custom Portal`,
    // start_timestamp is normalised from start_at in the store action
    start: ev.start_timestamp,
    end: ev.end_timestamp,
    backgroundColor: 'transparent',
    borderColor: '#666666',
    textColor: '#d0d0d0',
    classNames: ['cross-system-event'],
    display: 'block',
    extendedProps: {
      isCrossSystem: true,
      crossSystemData: ev,
    },
  });

  /**
   * Format a cross-system event's time range for the Pod Schedule card.
   * e.g. "8:20 AM - 8:40 AM"
   *
   * @param {Object} ev
   * @returns {string}
   */
  const formatCrossSystemTime = (ev) => {
    const start = moment(ev.start_timestamp).tz('America/Phoenix').format('h:mm A');
    const end = moment(ev.end_timestamp).tz('America/Phoenix').format('h:mm A');
    return `${start} - ${end}`;
  };

  /**
   * Display title for a cross-system event.
   *
   * @param {Object} ev
   * @returns {string}
   */
  const getCrossSystemTitle = (ev) => ev.title_name || ev.title || 'Occupied';

  return {
    loadCrossSystemDay,
    loadCrossSystemRange,
    getCrossSystemEventsForDate,
    toFullCalendarEvent,
    formatCrossSystemTime,
    getCrossSystemTitle,
  };
}
